import { QueryClient, QueryCache, MutationCache, type DefaultOptions, type Mutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isAppError } from '@/lib/api/errors';

/**
 * Format field errors for toast display
 * Returns a formatted message with bullet points for multiple errors
 */
function formatFieldErrors(fieldErrors: Record<string, string[]>): string {
  const errors: string[] = [];

  for (const [, messages] of Object.entries(fieldErrors)) {
    errors.push(...messages);
  }

  if (errors.length === 1) {
    return errors[0] ?? '';
  }

  // Multiple errors - format as bullet points
  return errors.map((err) => `• ${err}`).join('\n');
}

/**
 * Mutation meta type for controlling global error handling
 */
export interface MutationMeta {
  /** Skip global error toast - handle error locally in component */
  skipGlobalErrorToast?: boolean;
}

/**
 * Global error handler for queries and mutations
 * Displays toast notifications for errors (except validation errors handled in forms)
 */
function handleGlobalError(
  error: unknown,
  _query?: unknown,
  mutation?: Mutation<unknown, unknown, unknown, unknown>
): void {
  // Check if mutation wants to skip global error toast
  const meta = mutation?.meta as MutationMeta | undefined;
  if (meta?.skipGlobalErrorToast) {
    return;
  }

  // Skip 401 - handled by axios interceptor (redirect to login)
  if (isAppError(error) && error.statusCode === 401) {
    return;
  }

  // Show toast based on error type
  if (isAppError(error)) {
    // Handle validation errors with field errors
    if (error.isValidationError && error.fieldErrors) {
      const formattedErrors = formatFieldErrors(error.fieldErrors);
      toast.error(formattedErrors, {
        duration: 6000, // Longer duration for validation errors
        style: {
          whiteSpace: 'pre-line', // Preserve line breaks for bullet points
        },
      });
      return;
    }

    switch (error.statusCode) {
      case 403:
        toast.error('No tienes permisos para realizar esta acción');
        break;
      case 404:
        toast.error('El recurso solicitado no existe');
        break;
      case 500:
        toast.error('Error del servidor. Intenta más tarde.');
        break;
      default:
        toast.error(error.message);
    }
  } else {
    toast.error('Error de conexión. Verifica tu internet.');
  }
}

/**
 * Default options for React Query
 */
const defaultOptions: DefaultOptions = {
  queries: {
    // Data is considered fresh for 1 minute
    staleTime: 60 * 1000,

    // Cache data for 5 minutes
    gcTime: 5 * 60 * 1000,

    // Don't refetch on window focus (manual control)
    refetchOnWindowFocus: false,

    // Retry failed requests
    retry: (failureCount, error) => {
      // Don't retry on 4xx errors (client errors)
      if (isAppError(error) && error.statusCode >= 400 && error.statusCode < 500) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },

    // Retry delay with exponential backoff
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },
  mutations: {
    // Don't retry mutations by default
    retry: false,
  },
};

/**
 * Create a new QueryClient instance
 * Use this function to create the client in your app
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions,
    queryCache: new QueryCache({
      onError: (error, query) => handleGlobalError(error, query),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => handleGlobalError(error, undefined, mutation),
    }),
  });
}

/**
 * Stale times for different entities
 * Use these when you need custom stale times
 */
export const STALE_TIMES = {
  // User data - rarely changes
  user: 5 * 60 * 1000, // 5 minutes

  // Reference data (GADs, budget items) - changes infrequently
  reference: 2 * 60 * 1000, // 2 minutes

  // Transactional data - changes frequently
  transactional: 30 * 1000, // 30 seconds

  // Real-time data - always fresh
  realtime: 0,
} as const;
